import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ROLES } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LEVELS = ["None", "View", "Edit", "Manage"];
const LevelSelect = ({ value, onChange, allowDefault, testId }) => (
  <Select value={value === null || value === undefined ? "d" : String(value)} onValueChange={(v) => onChange(v === "d" ? null : Number(v))}>
    <SelectTrigger className="h-9 w-[110px]" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {allowDefault && <SelectItem value="d">Role default</SelectItem>}
      {LEVELS.map((l, i) => <SelectItem key={l} value={String(i)}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const UserOverrides = ({ features, roles }) => {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [ov, setOv] = useState({});
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role !== "admin"))); }, []);
  const u = users.find((x) => x.id === uid);
  const pick = (id) => { setUid(id); setOv(users.find((x) => x.id === id)?.permission_overrides || {}); };
  const save = async () => {
    try {
      await api.put(`/users/${uid}/permissions`, { overrides: ov });
      setUsers((x) => x.map((y) => (y.id === uid ? { ...y, permission_overrides: ov } : y)));
      toast.success("Overrides saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="space-y-4 rounded-2xl border bg-card p-5" data-testid="user-overrides">
      <h3 className="font-bold">Individual overrides</h3>
      <Select value={uid || undefined} onValueChange={pick}>
        <SelectTrigger className="max-w-sm" data-testid="override-user-select"><SelectValue placeholder="Select a user" /></SelectTrigger>
        <SelectContent>{users.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} · {ROLES[x.role]}</SelectItem>)}</SelectContent>
      </Select>
      {u && (
        <>
          <div className="divide-y rounded-xl border">
            {Object.entries(features).map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                <span>{label}<span className="ml-2 text-xs text-muted-foreground">(role: {LEVELS[roles[u.role]?.[k] || 0]})</span></span>
                <LevelSelect allowDefault value={ov[k]} onChange={(v) => setOv((o) => { const n = { ...o }; if (v === null) delete n[k]; else n[k] = v; return n; })} testId={`override-${k}`} />
              </div>
            ))}
          </div>
          <button onClick={save} data-testid="override-save-btn" className="rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground">Save overrides for {u.name}</button>
        </>
      )}
    </section>
  );
};

export default function Permissions() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/permissions").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  const roles = Object.keys(d.roles);
  const set = async (role, k, v) => {
    const perms = { ...d.roles[role], [k]: v };
    try {
      const { data } = await api.put(`/permissions/roles/${role}`, { perms });
      setD({ ...d, roles: { ...d.roles, [role]: data } });
      toast.success(`${ROLES[role]} updated`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Administration</p>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Permissions</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Choose what each role can do. View = see it, Edit = add / sign up / carry out, Manage = full control. Admin always has full access. Changes apply on the user's next page load.</p>
      </div>
      <section className="overflow-x-auto rounded-2xl border bg-card" data-testid="role-matrix">
        <table className="w-full min-w-[900px] text-sm">
          <thead><tr className="border-b text-left text-xs uppercase tracking-widest text-muted-foreground"><th className="px-4 py-3">Function</th>{roles.map((r) => <th key={r} className="px-2 py-3">{ROLES[r]}</th>)}</tr></thead>
          <tbody>
            {Object.entries(d.features).map(([k, label]) => (
              <tr key={k} className="border-b last:border-0">
                <td className="px-4 py-2 font-medium">{label}</td>
                {roles.map((r) => <td key={r} className="px-2 py-2"><LevelSelect value={d.roles[r][k] || 0} onChange={(v) => set(r, k, v ?? 0)} testId={`perm-${r}-${k}`} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <UserOverrides features={d.features} roles={d.roles} />
    </div>
  );
}
